/**
 * Wallets Service for CoinVAT API
 * Handles Web3 wallet management, connections, balances, and transactions
 */
import { apiClient } from './api-client';
import type {
  SignatureVerificationRequest,
  SignatureVerificationResponse,
  WalletConnection,
  WalletResponse,
  WalletUpdate,
  WalletBalanceResponse,
  WalletTransactionResponse,
  WalletTransactionParams,
} from './types';

export class WalletsService {
  /**
   * Generate message for wallet signature verification
   */
  async getVerificationMessage(request: SignatureVerificationRequest): Promise<SignatureVerificationResponse> {
    return apiClient.post<SignatureVerificationResponse>('/api/v1/wallets/verify-message', request);
  }

  /**
   * Connect a Web3 wallet to user account
   */
  async connectWallet(walletData: WalletConnection): Promise<WalletResponse> {
    return apiClient.post<WalletResponse>('/api/v1/wallets/connect', walletData);
  }

  /**
   * List user's connected wallets
   */
  async listWallets(): Promise<WalletResponse[]> {
    return apiClient.get<WalletResponse[]>('/api/v1/wallets/');
  }

  /**
   * Get specific wallet details
   */
  async getWallet(walletId: number): Promise<WalletResponse> {
    return apiClient.get<WalletResponse>(`/api/v1/wallets/${walletId}`);
  }

  /**
   * Update wallet settings
   */
  async updateWallet(walletId: number, updateData: WalletUpdate): Promise<WalletResponse> {
    return apiClient.put<WalletResponse>(`/api/v1/wallets/${walletId}`, updateData);
  }

  /**
   * Disconnect wallet from account
   */
  async disconnectWallet(walletId: number): Promise<{ message: string }> {
    return apiClient.delete<{ message: string }>(`/api/v1/wallets/${walletId}`);
  }

  /**
   * Get wallet balance and token holdings
   */
  async getWalletBalance(walletId: number): Promise<WalletBalanceResponse> {
    return apiClient.get<WalletBalanceResponse>(`/api/v1/wallets/${walletId}/balance`);
  }

  /**
   * Get wallet transaction history
   */
  async getWalletTransactions(
    walletId: number,
    params?: WalletTransactionParams
  ): Promise<WalletTransactionResponse[]> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    
    const queryString = queryParams.toString();
    const endpoint = queryString 
      ? `/api/v1/wallets/${walletId}/transactions?${queryString}`
      : `/api/v1/wallets/${walletId}/transactions`;
    
    return apiClient.get<WalletTransactionResponse[]>(endpoint);
  }

  /**
   * Manually refresh wallet balance
   */
  async refreshWalletBalance(walletId: number): Promise<{ message: string; balance_updated: boolean }> {
    return apiClient.post<{ message: string; balance_updated: boolean }>(
      `/api/v1/wallets/${walletId}/refresh-balance`
    );
  }

  // Utility methods for wallet management

  /**
   * Get primary wallet for the user
   */
  async getPrimaryWallet(): Promise<WalletResponse | null> {
    try {
      const wallets = await this.listWallets();
      return wallets.find(wallet => wallet.is_primary) || null;
    } catch (error) {
      console.error('Error getting primary wallet:', error);
      return null;
    }
  }

  /**
   * Set a wallet as primary
   */
  async setPrimaryWallet(walletId: number): Promise<WalletResponse> {
    return this.updateWallet(walletId, { is_primary: true });
  }

  /**
   * Enable/disable trading for a wallet
   */
  async toggleWalletTrading(walletId: number, enabled: boolean): Promise<WalletResponse> {
    return this.updateWallet(walletId, { is_trading_enabled: enabled });
  }

  /**
   * Get total portfolio value across all wallets
   */
  async getTotalPortfolioValue(): Promise<number> {
    try {
      const wallets = await this.listWallets();
      return wallets.reduce((total, wallet) => {
        return total + (wallet.estimated_balance_usd || 0);
      }, 0);
    } catch (error) {
      console.error('Error calculating total portfolio value:', error);
      return 0;
    }
  }

  /**
   * Get wallets by blockchain
   */
  async getWalletsByBlockchain(blockchain: string): Promise<WalletResponse[]> {
    try {
      const wallets = await this.listWallets();
      return wallets.filter(wallet => wallet.blockchain.toLowerCase() === blockchain.toLowerCase());
    } catch (error) {
      console.error('Error getting wallets by blockchain:', error);
      return [];
    }
  }

  /**
   * Check if a wallet address is already connected
   */
  async isWalletConnected(address: string, blockchain: string): Promise<boolean> {
    try {
      const wallets = await this.listWallets();
      return wallets.some(wallet => 
        wallet.address.toLowerCase() === address.toLowerCase() && 
        wallet.blockchain.toLowerCase() === blockchain.toLowerCase()
      );
    } catch (error) {
      console.error('Error checking wallet connection:', error);
      return false;
    }
  }

  /**
   * Get wallet by address and blockchain
   */
  async findWalletByAddress(address: string, blockchain: string): Promise<WalletResponse | null> {
    try {
      const wallets = await this.listWallets();
      return wallets.find(wallet => 
        wallet.address.toLowerCase() === address.toLowerCase() && 
        wallet.blockchain.toLowerCase() === blockchain.toLowerCase()
      ) || null;
    } catch (error) {
      console.error('Error finding wallet by address:', error);
      return null;
    }
  }
}

// Export singleton instance
export const walletsService = new WalletsService();